import { ChevronDown, Compass } from 'lucide-react';
import { useId } from 'react';
import type { ReactNode } from 'react';
import { useTourControls } from '../hooks/TourContext';
import { tx } from '../i18n';

/**
 * One heading and the text under it, closed until somebody wants it.
 *
 * <p>Native {@code <details>} rather than a toggle of our own: it opens with the keyboard, it is
 * announced correctly, it prints open when a browser prints the page, and the browser's own find
 * can open it to show a match. There is no reason to reimplement any of that.
 */
function FooterBlock({ title, children }: { title: string; children: ReactNode }) {
  const id = useId();
  return (
    <details className="footer-block" id={id}>
      <summary className="footer-block__summary">
        {title}
        <ChevronDown size={16} aria-hidden="true" className="footer-block__chevron" />
      </summary>
      <div className="footer-block__body small">{children}</div>
    </details>
  );
}

/**
 * The attribution the open licences require, and the limits of what this product claims - split
 * into the four things a reader actually comes here looking for.
 *
 * <p>All of it used to be one wall of small print, which is the same as publishing none of it: a
 * reader who wants to know where the crime figures come from should not have to read a licence
 * notice and a disclaimer first. Each heading is closed until it is asked for, and the one line
 * that must not be missed - this is not advice - stays open above them.
 */
/** Who is selling this, from the deployment's own configuration. Empty until it is set. */
const BUSINESS = {
  name: String(import.meta.env.VITE_BUSINESS_NAME ?? '').trim(),
  legalName: String(import.meta.env.VITE_BUSINESS_LEGAL_NAME ?? '').trim(),
  companyNumber: String(import.meta.env.VITE_BUSINESS_COMPANY_NUMBER ?? '').trim(),
  vatNumber: String(import.meta.env.VITE_BUSINESS_VAT_NUMBER ?? '').trim(),
  address: String(import.meta.env.VITE_BUSINESS_ADDRESS ?? '').trim(),
  email: String(import.meta.env.VITE_BUSINESS_EMAIL ?? '').trim(),
};

export default function SiteFooter() {
  /*
   * The way back into the tour, for whoever pressed Skip.
   *
   * Rendered only when the screen the reader is on has a tour, so it is never a link that does
   * nothing - and here rather than in the header, where the one row of controls already carries
   * the language switch and the comparison counter and has no room on a phone.
   */
  const { available, again } = useTourControls();

  return (
    <footer className="site-footer">
      <div className="site-footer__inner">
        <p className="site-footer__brand">
          <strong>ScanHome</strong> — {tx('England', 'İngiltere', 'Anglia', 'Inglaterra', 'Inglaterra')}
        </p>

        <p className="small">
          {tx(
            'Scores describe an address, its surroundings and the certificates registered against it. They are not a survey, a valuation, or legal advice about a specific tenancy.',
            'Puanlar bir adresi, çevresini ve ona kayıtlı sertifikaları anlatır. Bir ekspertiz, değerleme ya da belirli bir kira sözleşmesi hakkında hukuki tavsiye değildir.',
            'Oceny opisują adres, jego otoczenie i świadectwa do niego zarejestrowane. Nie są ekspertyzą budowlaną, wyceną ani poradą prawną dotyczącą konkretnej umowy najmu.',
            'As pontuações descrevem uma morada, o que a rodeia e os certificados registados para ela. Não são uma vistoria, uma avaliação nem aconselhamento jurídico sobre um contrato de arrendamento concreto.',
            'Las puntuaciones describen una dirección, su entorno y los certificados registrados a su nombre. No son una inspección, una tasación ni asesoramiento legal sobre un contrato de alquiler concreto.',
          )}
        </p>

        {available ? (
          <button type="button" className="footer-tour" onClick={again}>
            <Compass size={14} aria-hidden="true" />{' '}
            {tx(
              'Show the guided tour again',
              'Rehberli turu tekrar göster',
              'Pokaż przewodnik ponownie',
              'Mostrar a visita guiada outra vez',
              'Ver el recorrido guiado otra vez',
            )}
          </button>
        ) : null}

        <div className="site-footer__blocks">
          <FooterBlock
            title={tx('Data sources', 'Veri kaynakları', 'Źródła danych', 'Fontes de dados', 'Fuentes de datos')}
          >
            <ul className="footer-list">
              <li>
                <strong>Postcodes.io</strong> —{' '}
                {tx('postcode coordinates and administrative areas', 'posta kodu koordinatları ve idari bölgeler',
                  'współrzędne kodów pocztowych i obszary administracyjne',
                  'coordenadas de códigos postais e áreas administrativas',
                  'coordenadas de códigos postales y áreas administrativas')}
              </li>
              <li>
                <strong>data.police.uk</strong> —{' '}
                {tx('street level recorded crime', 'sokak düzeyinde kayıtlı suçlar',
                  'przestępczość odnotowana na poziomie ulicy',
                  'criminalidade registada ao nível da rua',
                  'delincuencia registrada a nivel de calle')}
              </li>
              <li>
                <strong>{tx('Energy Performance of Buildings Register', 'Bina Enerji Performansı kayıtları',
                  'Rejestr charakterystyki energetycznej budynków',
                  'Registo de certificados energéticos de edifícios',
                  'Registro de certificados energéticos de edificios')}</strong> —{' '}
                {tx('EPC bands, the fabric of the home and estimated running costs',
                  'EPC bandı, konutun yapısı ve tahmini giderler',
                  'klasy EPC, konstrukcja mieszkania i szacowane koszty eksploatacji',
                  'classes EPC, a construção da casa e custos estimados',
                  'letras EPC, la construcción de la vivienda y costes estimados')}
              </li>
              <li>
                <strong>Ofcom Connected Nations</strong> —{' '}
                {tx('fixed broadband coverage per postcode', 'posta kodu bazında sabit internet kapsaması',
                  'zasięg internetu stacjonarnego dla kodu pocztowego',
                  'cobertura de internet fixa por código postal',
                  'cobertura de internet fija por código postal')}
              </li>
              <li>
                <strong>Get Information about Schools, Ofsted</strong> —{' '}
                {tx('schools nearby and their latest judgements', 'yakındaki okullar ve son değerlendirmeleri',
                  'szkoły w pobliżu i ich najnowsze oceny',
                  'escolas nas imediações e as suas avaliações mais recentes',
                  'colegios cercanos y sus últimas valoraciones')}
              </li>
              <li>
                <strong>NaPTAN, MHCLG, VOA</strong> —{' '}
                {tx('transport stops, council tax levels and the local band mix',
                  'toplu taşıma durakları, council tax tutarları ve yerel band dağılımı',
                  'przystanki komunikacji, stawki podatku lokalnego i lokalny rozkład przedziałów',
                  'paragens de transporte, taxas de imposto municipal e distribuição local por escalão',
                  'paradas de transporte, tipos del impuesto municipal y reparto local por tramos')}
              </li>
            </ul>
            <p className="small muted">
              {tx(
                'Contains public sector information licensed under the Open Government Licence v3.0. Street photos © Google.',
                'Open Government Licence v3.0 kapsamında lisanslı kamu verisi içerir. Sokak fotoğrafları © Google.',
                'Zawiera informacje sektora publicznego objęte licencją Open Government Licence v3.0. Zdjęcia ulic © Google.',
                'Contém informação do setor público licenciada ao abrigo da Open Government Licence v3.0. Fotografias de rua © Google.',
                'Contiene información del sector público con licencia Open Government Licence v3.0. Fotos de calle © Google.',
              )}
            </p>
          </FooterBlock>

          <FooterBlock
            title={tx('Methodology', 'Yöntem', 'Metodyka', 'Metodologia', 'Metodología')}
          >
            <p>
              {tx(
                'Every home is scored with the same published formula: each category is scored 0 to 100 against other homes in the same English region, multiplied by its weight, and added together. A source that could not be read carries no weight rather than counting as a zero.',
                'Her konut aynı yayımlanmış formülle puanlanır: her kategori, aynı İngiltere bölgesindeki diğer konutlara göre 0-100 arasında puanlanır, ağırlığıyla çarpılır ve toplanır. Okunamayan bir kaynak sıfır sayılmaz; hiç ağırlık taşımaz.',
                'Każde mieszkanie jest oceniane tym samym opublikowanym wzorem: każda kategoria jest oceniana od 0 do 100 na tle innych mieszkań w tym samym regionie Anglii, mnożona przez swoją wagę i sumowana. Źródło, którego nie udało się odczytać, nie ma wagi, zamiast liczyć się jako zero.',
                'Todas as casas são pontuadas com a mesma fórmula publicada: cada categoria é pontuada de 0 a 100 face a outras casas da mesma região de Inglaterra, multiplicada pelo seu peso e somada. Uma fonte que não pôde ser lida não tem peso, em vez de contar como zero.',
                'Toda vivienda se puntúa con la misma fórmula publicada: cada categoría se puntúa de 0 a 100 frente a otras viviendas de la misma región de Inglaterra, se multiplica por su peso y se suma. Una fuente que no se ha podido leer no pesa nada, en lugar de contar como un cero.',
              )}
            </p>
            <p>
              {tx(
                'Every report shows its own weights, the scoring model that produced them, and the date each source was read.',
                'Her rapor kendi ağırlıklarını, bunları üreten puanlama modelini ve her kaynağın okunma tarihini gösterir.',
                'Każdy raport pokazuje własne wagi, model oceny, który je zastosował, oraz datę odczytu każdego źródła.',
                'Cada relatório mostra os seus pesos, o modelo de pontuação que os produziu e a data em que cada fonte foi lida.',
                'Cada informe muestra sus pesos, el modelo de puntuación que los produjo y la fecha en que se leyó cada fuente.',
              )}
            </p>
          </FooterBlock>

          <FooterBlock
            title={tx('Terms and conditions', 'Şartlar ve koşullar', 'Regulamin',
              'Termos e condições', 'Términos y condiciones')}
          >
            <p>
              {tx(
                'ScanHome reports public data about an address. It is not a survey, a valuation, a reference on a landlord, or legal or financial advice, and it cannot tell you the condition of the home inside. Public registers are sometimes incomplete or out of date; where we know a figure is estimated or missing, the report says so rather than filling the gap.',
                'ScanHome bir adres hakkındaki kamu verilerini aktarır. Ekspertiz, değerleme, ev sahibi referansı ya da hukuki veya finansal tavsiye değildir ve konutun içindeki durumu söyleyemez. Kamu kayıtları zaman zaman eksik ya da güncel olmayabilir; bir değerin tahmini ya da eksik olduğunu bildiğimizde rapor boşluğu doldurmak yerine bunu söyler.',
                'ScanHome przedstawia dane publiczne o adresie. Nie jest ekspertyzą, wyceną, referencją o właścicielu ani poradą prawną czy finansową i nie powie Ci, w jakim stanie jest wnętrze. Rejestry publiczne bywają niekompletne lub nieaktualne; jeśli wiemy, że dana liczba jest szacunkowa albo jej brak, raport to mówi, zamiast wypełniać lukę.',
                'O ScanHome apresenta dados públicos sobre uma morada. Não é uma vistoria, uma avaliação, uma referência sobre o senhorio, nem aconselhamento jurídico ou financeiro, e não lhe pode dizer o estado do interior. Os registos públicos são por vezes incompletos ou desatualizados; quando sabemos que um valor é estimado ou está em falta, o relatório di-lo em vez de preencher a lacuna.',
                'ScanHome presenta datos públicos sobre una dirección. No es una inspección, una tasación, una referencia sobre el propietario, ni asesoramiento legal o financiero, y no puede decirte el estado del interior. Los registros públicos a veces están incompletos o desactualizados; cuando sabemos que una cifra es estimada o falta, el informe lo dice en lugar de rellenar el hueco.',
              )}
            </p>

            {/*
              * The operative terms are in English and are not translated.
              *
              * Everything else on this site is written in five languages, and this is the one
              * place where that would do harm: five translations of a contract are five contracts
              * that can disagree with each other, and the one a reader relies on would be
              * whichever they happened to open. The contract is English law, so it is stated once,
              * in English, and the line above it says so in the reader's own language.
              */}
            <p className="small muted">
              {tx(
                'The terms below are the contract between you and us. They are written in English and are not translated, so that there is only one version to rely on.',
                'Aşağıdaki koşullar sizinle aramızdaki sözleşmedir. Güvenilecek tek bir metin olması için İngilizce yazılmıştır ve çevrilmemiştir.',
                'Poniższe warunki stanowią umowę między Tobą a nami. Są napisane po angielsku i nie są tłumaczone, aby istniała tylko jedna wiążąca wersja.',
                'Os termos abaixo são o contrato entre si e nós. Estão escritos em inglês e não são traduzidos, para que exista apenas uma versão na qual confiar.',
                'Los términos siguientes son el contrato entre usted y nosotros. Están redactados en inglés y no se traducen, para que solo haya una versión en la que confiar.',
              )}
            </p>

            <ol className="footer-terms small">
              <li>
                <strong>Who we are.</strong> ScanHome is operated by{' '}
                {BUSINESS.legalName || BUSINESS.name || 'sunmaxdigital'}
                {BUSINESS.address ? `, ${BUSINESS.address}` : ''}. Questions and complaints go to{' '}
                {BUSINESS.email ? <a href={`mailto:${BUSINESS.email}`}>{BUSINESS.email}</a> : 'the contact address shown above'}.
              </li>
              <li>
                <strong>Accuracy.</strong> We report what the public registers say on the day we read
                them. Those registers are sometimes incomplete, out of date, or recorded against a
                neighbouring address. We show the date and the match level for each figure, and we
                label an estimate as an estimate; we do not guarantee that a figure is correct, nor
                that a home is safe, lawful, or suitable for you.
              </li>
              <li>
                <strong>What this is not.</strong> Not a survey, a valuation, a reference on a
                landlord, or legal, financial or tax advice. Decisions about renting or buying a home
                are yours, and a report is one input to them.
              </li>
              <li>
                <strong>Our responsibility to you.</strong> We do not exclude liability for death or
                personal injury caused by our negligence, for fraud, or for anything else that cannot
                lawfully be excluded. Beyond that, ScanHome is provided free of charge and as it is:
                we give no warranty that a report is complete or correct, and we are not liable for
                losses arising from a decision you took about a property.
              </li>
              <li>
                <strong>Using the site fairly.</strong> Reports are for your own use. Do not scrape
                the site, extract data in bulk, or resell or republish reports.
              </li>
              <li>
                <strong>Changes.</strong> We may change these terms. The version published here when
                you use ScanHome is the one that applies.
              </li>
              <li>
                <strong>Law.</strong> These terms are governed by the law of England and Wales, and
                the courts of England and Wales have jurisdiction. Your statutory rights as a
                consumer are not affected.
              </li>
            </ol>
          </FooterBlock>

          <FooterBlock title={tx('Privacy', 'Gizlilik', 'Prywatność', 'Privacidade', 'Privacidad')}>
            <p>
              {tx(
                'No account is needed and none is offered. Your comparison and your saved reports are kept in this browser, not against a profile; clearing your site data removes them. Addresses you look up are used to run the report and to count how often the public sources are called, and are not sold or shared for advertising.',
                'Hesap gerekmez ve sunulmaz. Karşılaştırmanız ve kaydettiğiniz raporlar bir profilde değil, bu tarayıcıda tutulur; site verilerinizi temizlemek bunları siler. Sorguladığınız adresler raporu üretmek ve kamu kaynaklarının ne sıklıkla çağrıldığını saymak için kullanılır; reklam için satılmaz ya da paylaşılmaz.',
                'Konto nie jest potrzebne ani oferowane. Twoje porównanie i zapisane raporty są przechowywane w tej przeglądarce, a nie przy profilu; wyczyszczenie danych witryny je usuwa. Adresy, które sprawdzasz, służą do przygotowania raportu i do policzenia, jak często odpytujemy źródła publiczne, i nie są sprzedawane ani udostępniane do celów reklamowych.',
                'Não é preciso conta e nenhuma é oferecida. A sua comparação e os relatórios guardados ficam neste navegador, não num perfil; limpar os dados do site remove-os. As moradas que consulta servem para gerar o relatório e para contar com que frequência as fontes públicas são chamadas, e não são vendidas nem partilhadas para publicidade.',
                'No hace falta cuenta y no se ofrece ninguna. Tu comparación y tus informes guardados se quedan en este navegador, no en un perfil; borrar los datos del sitio los elimina. Las direcciones que consultas se usan para generar el informe y para contar con qué frecuencia se llama a las fuentes públicas, y no se venden ni se comparten con fines publicitarios.',
              )}
            </p>
          </FooterBlock>
        </div>

        {/*
          * Who is actually selling this.
          *
          * A UK business selling online has to give its trading name, a geographic address and an
          * email address, and a limited company has to give its registered name and number. A
          * copyright line is none of those. It comes from configuration rather than the source,
          * because these are facts about a company and not decisions a developer should be
          * inventing; with nothing set, nothing is claimed.
          */}
        <p className="small muted footer-identity">
          <span>© {new Date().getFullYear()} {BUSINESS.name || 'ScanHome'}</span>
          {BUSINESS.legalName ? <span>{BUSINESS.legalName}</span> : null}
          {BUSINESS.companyNumber ? (
            <span>
              {tx(
                `Registered in England and Wales, company no. ${BUSINESS.companyNumber}`,
                `İngiltere ve Galler'de kayıtlı, şirket no. ${BUSINESS.companyNumber}`,
                `Zarejestrowana w Anglii i Walii, nr ${BUSINESS.companyNumber}`,
                `Registada em Inglaterra e País de Gales, n.º ${BUSINESS.companyNumber}`,
                `Registrada en Inglaterra y Gales, n.º ${BUSINESS.companyNumber}`,
              )}
            </span>
          ) : null}
          {BUSINESS.vatNumber ? <span>{`VAT ${BUSINESS.vatNumber}`}</span> : null}
          {BUSINESS.address ? <span>{BUSINESS.address}</span> : null}
          {BUSINESS.email ? (
            <a href={`mailto:${BUSINESS.email}`}>{BUSINESS.email}</a>
          ) : null}
        </p>
      </div>
    </footer>
  );
}
